import { Box, Button, ButtonGroup, Chip, LinearProgress, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { RelEntity } from '../object-actions/types/types';
import SpiderChart from './SpiderChart';

interface CategoryStat {
    category: string;
    total_response: number;
    response_count: number;
    average_response: number;
}

interface ResponseGraphsProps {
    entity?: any;
    data?: CategoryStat[];
    submissions?: Array<RelEntity<'AssessmentSubmission'>>;
    title?: string;
    height?: number;
    maxValue?: number;
}

export const AssessmentDomainMap: Record<string, { name: string; description: string; max_value: number; question_count: number }> = {
    category_performance_mindset: {
        name: 'Performance Mindset',
        description: 'Reflects how intentionally and strategically an athlete prepares for performance—setting process goals, establishing routines, focusing on controllable factors, and using visualization to optimize readiness.',
        max_value: 5,
        question_count: 6,
    },
    category_emotional_regulation: {
        name: 'Emotional Regulation',
        description: 'Represents an athlete\'s ability to recognize, manage, and recover from emotional stressors encountered in sport contexts.',
        max_value: 5,
        question_count: 6,
    },
    category_confidence: {
        name: 'Self Belief',
        description: 'Captures self-belief and perceived capability to perform successfully, even in challenging or uncertain situations.',
        max_value: 5,
        question_count: 6,
    },
    category_resilience_motivation: {
        name: 'Resilience & Motivation',
        description: 'Combines perseverance, adaptability, and intrinsic drive—reflecting how athletes persist through adversity and stay engaged with long-term goals.',
        max_value: 5,
        question_count: 6,
    },
    category_concentration: {
        name: 'Concentration',
        description: 'Reflects the athlete\'s ability to direct and sustain attention on task-relevant cues while minimizing distractions.',
        max_value: 5,
        question_count: 6,
    },
    category_leadership: {
        name: 'Leadership',
        description: 'Encompasses initiative, accountability, and the ability to inspire or guide others through behavior and example.',
        max_value: 5,
        question_count: 6,
    },
    category_mental_wellbeing: {
        name: 'Mental Well-being',
        description: 'Reflects overall enjoyment, balance, and emotional health within sport participation.',
        max_value: 5,
        question_count: 8,
    },
    category_communication: {
        name: 'Communication',
        description: 'Represents interpersonal clarity, active listening, and constructive interaction within a team or group environment.',
        max_value: 5,
        question_count: 6,
    },
};

const structureSpiderData = (entity: any): CategoryStat[] => {
    if (!entity) return [];

    const entityData = entity?.entity || entity;
    if (!entityData) return [];

    const spiderData: CategoryStat[] = [];
    for (const [field_name, domainInfo] of Object.entries(AssessmentDomainMap)) {
        if (entityData[field_name]) {
            const score = entityData[field_name];
            const numScore = typeof score === 'number' ? score : (typeof score === 'string' ? parseFloat(score) : 0);
            if (!isNaN(numScore)) {
                spiderData.push({
                    category: domainInfo.name,
                    average_response: numScore,
                    response_count: domainInfo.question_count,
                    total_response: numScore * domainInfo.question_count
                });
            }
        }
    }

    return spiderData;
};

function getSubmittedSorted(submissions: Array<RelEntity<'AssessmentSubmission'>>): Array<RelEntity<'AssessmentSubmission'>> {
    return submissions
        .filter(sub => (sub.entity as any)?.submitted_at)
        .sort((a, b) => {
            const dateA = (a.entity as any)?.submitted_at ? new Date((a.entity as any).submitted_at).getTime() : 0;
            const dateB = (b.entity as any)?.submitted_at ? new Date((b.entity as any).submitted_at).getTime() : 0;
            return dateA - dateB;
        });
}

const getDomainInfo = (category: string) => {
    for (const [, domainInfo] of Object.entries(AssessmentDomainMap)) {
        if (domainInfo.name === category) {
            return domainInfo;
        }
    }
    if (category.startsWith('category_')) {
        return AssessmentDomainMap[category];
    }
    const fieldName = `category_${category.toLowerCase().replace(/\s+/g, '_')}`;
    return AssessmentDomainMap[fieldName];
};

const formatCategoryName = (category: string): string => {
    const domainInfo = getDomainInfo(category);
    if (domainInfo) {
        return domainInfo.name;
    }
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getCategoryDescription = (category: string): string => {
    const domainInfo = getDomainInfo(category);
    return domainInfo?.description || '';
};

const ResponseGraphs: React.FC<ResponseGraphsProps> = ({
    entity,
    data,
    submissions,
    title = undefined,
    height = 250,
    maxValue = 5
}) => {
    const [viewMode, setViewMode] = useState<'spider' | 'linear'>('spider');

    const sortedSubmissions = useMemo(() => {
        if (!submissions || submissions.length === 0) return [];
        return getSubmittedSorted(submissions);
    }, [submissions]);

    const hasComparison = sortedSubmissions.length > 1;

    const preData = useMemo(() => {
        if (sortedSubmissions.length > 0) {
            return structureSpiderData(sortedSubmissions[0].entity);
        }
        return [];
    }, [sortedSubmissions]);

    const postData = useMemo(() => {
        if (sortedSubmissions.length > 1) {
            return structureSpiderData(sortedSubmissions[sortedSubmissions.length - 1].entity);
        }
        return [];
    }, [sortedSubmissions]);

    const singleData = useMemo(() => {
        if (data) return data;
        if (entity) return structureSpiderData(entity);
        if (preData.length > 0 && postData.length === 0) return preData;
        return [];
    }, [entity, data, preData, postData]);

    const spiderSeries = useMemo(() => {
        if (hasComparison && preData.length > 0 && postData.length > 0) {
            return [
                { label: 'Pre-Assessment', data: preData, color: '#90caf9' },
                { label: 'Post-Assessment', data: postData, color: '#1976d2' },
            ];
        }
        return undefined;
    }, [hasComparison, preData, postData]);

    const displayData = hasComparison ? postData : singleData;

    if ((!displayData || displayData.length === 0) && !spiderSeries) {
        return (
            <Typography variant="h6" color="text.secondary">
                No data available for response graphs
            </Typography>
        );
    }

    return (
        <Box data-testid="response-graphs">
            {title && (
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
                    {title}
                </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <ButtonGroup variant="outlined" aria-label="view mode toggle">
                    <Button
                        size={'small'}
                        onClick={() => setViewMode('spider')}
                        variant={viewMode === 'spider' ? 'contained' : 'outlined'}
                    >
                        Spider Chart
                    </Button>
                    <Button
                        size={'small'}
                        onClick={() => setViewMode('linear')}
                        variant={viewMode === 'linear' ? 'contained' : 'outlined'}
                    >
                        Linear Progress
                    </Button>
                </ButtonGroup>
            </Box>

            {viewMode === 'spider' ? (
                <SpiderChart
                    series={spiderSeries}
                    data={!spiderSeries ? displayData : undefined}
                    title={undefined}
                    height={height}
                    showLegend={false}
                />
            ) : (
                <Box>
                    {(hasComparison ? postData : displayData).map((item, index) => {
                        const domainInfo = getDomainInfo(item.category);
                        const formattedCategory = formatCategoryName(item.category);
                        const itemMaxValue = domainInfo?.max_value ?? maxValue;
                        const postValue = (item.average_response / itemMaxValue) * 100;
                        const description = getCategoryDescription(item.category);

                        const preItem = hasComparison
                            ? preData.find(p => p.category === item.category)
                            : undefined;
                        const preValue = preItem
                            ? (preItem.average_response / itemMaxValue) * 100
                            : undefined;
                        const change = preItem
                            ? item.average_response - preItem.average_response
                            : null;

                        return (
                            <Box key={index} sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="medium">
                                        {formattedCategory}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {hasComparison && preItem && (
                                            <Typography variant="caption" color="text.secondary">
                                                {preItem.average_response.toFixed(2)} &rarr;
                                            </Typography>
                                        )}
                                        <Typography variant="body2" color="text.secondary">
                                            {item.average_response.toFixed(2)} / {itemMaxValue}
                                        </Typography>
                                        {change !== null && (
                                            <Chip
                                                label={change > 0 ? `+${change.toFixed(2)}` : change.toFixed(2)}
                                                size="small"
                                                color={change > 0 ? 'success' : change < 0 ? 'error' : 'default'}
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </Box>
                                {hasComparison && preValue !== undefined && (
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(preValue, 100)}
                                        sx={{
                                            height: 6,
                                            borderRadius: 1,
                                            mb: 0.5,
                                            backgroundColor: 'action.disabledBackground',
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 1,
                                                backgroundColor: '#90caf9',
                                            },
                                        }}
                                    />
                                )}
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(postValue, 100)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 1,
                                        backgroundColor: 'action.disabledBackground',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 1,
                                        },
                                    }}
                                />
                                {description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {description}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                    {hasComparison && (
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 16, height: 6, borderRadius: 1, backgroundColor: '#90caf9' }} />
                                <Typography variant="caption" color="text.secondary">Pre-Assessment</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 16, height: 8, borderRadius: 1, backgroundColor: 'primary.main' }} />
                                <Typography variant="caption" color="text.secondary">Post-Assessment</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ResponseGraphs;
export type { CategoryStat };
